/**
 * User-Specific localStorage utilities
 *
 * Provides functions to store and retrieve data in localStorage
 * with user-specific keys to prevent data leakage between accounts
 * on the same device.
 */

import AuthService from '../services/authService';

/**
 * Get the current user ID from the auth token
 * Returns null if not authenticated
 */
function getCurrentUserId() {
  const token = AuthService.getAuthToken();
  if (!token) return null;

  try {
    // JWT tokens are base64 encoded with 3 parts: header.payload.signature
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded.userId || decoded.id || decoded.sub || null;
  } catch (e) {
    console.error('Error decoding auth token:', e);
    return null;
  }
}

/**
 * Generate a user-specific storage key
 * @param {string} baseKey - The base key name
 * @param {string|null} userId - Optional user ID (uses current user if not provided)
 * @returns {string} The user-specific key or the base key if no user
 */
export function getUserStorageKey(baseKey, userId = null) {
  const uid = userId || getCurrentUserId();
  return uid ? `${baseKey}_user_${uid}` : baseKey;
}

/**
 * Get a value from user-specific localStorage
 * @param {string} baseKey - The base key name
 * @param {*} defaultValue - Default value if not found
 * @returns {*} The parsed value or default
 */
export function getUserStorage(baseKey, defaultValue = null) {
  const key = getUserStorageKey(baseKey);
  try {
    const value = localStorage.getItem(key);
    if (value === null) return defaultValue;
    return JSON.parse(value);
  } catch (e) {
    console.error(`Error reading ${key} from localStorage:`, e);
    return defaultValue;
  }
}

/**
 * Set a value in user-specific localStorage
 * @param {string} baseKey - The base key name
 * @param {*} value - The value to store (will be JSON stringified)
 */
export function setUserStorage(baseKey, value) {
  const key = getUserStorageKey(baseKey);
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error(`Error writing ${key} to localStorage:`, e);
  }
}

/**
 * Remove a value from user-specific localStorage
 * @param {string} baseKey - The base key name
 */
export function removeUserStorage(baseKey) {
  const key = getUserStorageKey(baseKey);
  localStorage.removeItem(key);
}

/**
 * Migrate non-user-specific localStorage data to user-specific keys
 * Call this on login to migrate any existing data
 * @param {string[]} keysToMigrate - Array of base key names to migrate
 */
export function migrateToUserStorage(keysToMigrate) {
  const userId = getCurrentUserId();
  if (!userId) return;

  keysToMigrate.forEach(baseKey => {
    const oldValue = localStorage.getItem(baseKey);
    const newKey = getUserStorageKey(baseKey, userId);

    // Only migrate if old key exists and new key doesn't
    if (oldValue !== null && localStorage.getItem(newKey) === null) {
      localStorage.setItem(newKey, oldValue);
      console.log(`Migrated ${baseKey} to ${newKey}`);
    }

    // Remove old non-user-specific key
    localStorage.removeItem(baseKey);
  });
}

/**
 * Clear all user-specific data for the current user
 * Call this on logout if you want to clear cached data
 * @param {string[]} baseKeys - Array of base key names to clear
 */
export function clearUserStorage(baseKeys) {
  baseKeys.forEach(baseKey => {
    const key = getUserStorageKey(baseKey);
    localStorage.removeItem(key);
  });
}

const userStorage = {
  getUserStorageKey,
  getUserStorage,
  setUserStorage,
  removeUserStorage,
  migrateToUserStorage,
  clearUserStorage
};

export default userStorage;
